import { DASHBOARD_SHARED_SECTIONS } from "constants/constants";
import { FormattedMessage } from "react-intl";
import { getFormattedDate, getJobsInCategories } from "./functions";
import { JOB_CATEGORY_LABEL } from "constants/forms";
import InputDate from "core/inputs/input-date/InputDate";
import InputText from "core/inputs/input-text/InputText";
import InputSelect from "core/inputs/input-select/InputSelect";
import { Controller } from "utils/Controller";
import { buildDybProfileUrl, getEntityIsAllowed } from "utils/helper";
import moment from "moment";
const { ic, fdm, ed, ok, cl, ko } = DASHBOARD_SHARED_SECTIONS || {};

const renderHeaderLabel = ({ id, defaultMessage }) => ({
  label: {
    key: id,
    value: <FormattedMessage id={id} defaultMessage={defaultMessage} />,
  },
  id: id,
});

export const isObjectFiltersEmpty = (obj) => {
  if (!obj || typeof obj !== "object") return true;
  return Object.values(obj).every(
    (val) => (Array.isArray(val) && val.length === 0) || !val
  );
};

export const headerLabels = {
  card: renderHeaderLabel({ id: "card", defaultMessage: "Fiche" }),
  all: renderHeaderLabel({
    id: "cslt_str",
    defaultMessage: "Consultant / Sous-traitant",
  }),
  doyoubuzz: renderHeaderLabel({
    id: "doyoubuzz",
    defaultMessage: "Doyoubuzz",
  }),
  consultant: renderHeaderLabel({
    id: "consultant",
    defaultMessage: "Consultant",
  }),
  subcontractor: renderHeaderLabel({
    id: "subcontractor",
    defaultMessage: "Sous-traitant",
  }),
  branch: renderHeaderLabel({
    id: "branch",
    defaultMessage: "Filiale",
  }),
  branch_2: renderHeaderLabel({
    id: "branch_2",
    defaultMessage: "Filiale",
  }),
  manager: renderHeaderLabel({
    id: "manager",
    defaultMessage: "Manager",
  }),
  start_up_at: renderHeaderLabel({
    id: "start_up_at",
    defaultMessage: "Démarrage chez",
  }),
  date_of_start_up: renderHeaderLabel({
    id: "date_of_start_up",
    defaultMessage: "Date de démarrage",
  }),
  mission_name: renderHeaderLabel({
    id: "mission_name",
    defaultMessage: "Nom de la mission",
  }),
  ic_start: renderHeaderLabel({
    id: "ic_start",
    defaultMessage: "Début IC",
  }),
  start_date: renderHeaderLabel({
    id: "start_date",
    defaultMessage: "Date de début",
  }),
  end_date: renderHeaderLabel({
    id: "end_date",
    defaultMessage: "Date de fin",
  }),
  end_of_mission: renderHeaderLabel({
    id: "end_of_mission",
    defaultMessage: "Fin de mission",
  }),
  leaving_date_expected: renderHeaderLabel({
    id: "leaving_date_expected",
    defaultMessage: "Date de sortie prévue",
  }),
  long_leave_start_date: renderHeaderLabel({
    id: "long_leave_start_date",
    defaultMessage: "Date début de CL",
  }),
  provisional_return_date: renderHeaderLabel({
    id: "provisional_return_date",
    defaultMessage: "Date retour prévisionnelle",
  }),
  phone_number: renderHeaderLabel({
    id: "input_phone_number_label",
    defaultMessage: "Téléphone",
  }),
  mobility: renderHeaderLabel({
    id: "input_mobility_label",
    defaultMessage: "Mobilité",
  }),
  client: renderHeaderLabel({
    id: "client",
    defaultMessage: "Client",
  }),
  seniority_at_davidson: renderHeaderLabel({
    id: "seniority_at_davidson",
    defaultMessage: "Ancienneté chez Dav",
  }),
  salary: renderHeaderLabel({
    id: "salary",
    defaultMessage: "Salaire mensuel temps plein",
  }),
  last_fee: renderHeaderLabel({
    id: "last_fee",
    defaultMessage: "Dernier tarif",
  }),
  AT_fee: renderHeaderLabel({
    id: "at_fee",
    defaultMessage: "Tarif en AT",
  }),
  nat: renderHeaderLabel({
    id: "nat",
    defaultMessage: "Nationalité",
  }),
  job: renderHeaderLabel({
    id: "input_dav_function_label",
    defaultMessage: "Métier",
  }),
  email: renderHeaderLabel({
    id: "input_accounting_contact_email_label",
    defaultMessage: "E-mail",
  }),
  dyb: renderHeaderLabel({
    id: "dyb",
    defaultMessage: "DYB",
  }),
};

export const headerToDisplay = (section) => {
  const commonHeaderLabels = [
    headerLabels.card,
    headerLabels.doyoubuzz,
    section === DASHBOARD_SHARED_SECTIONS.ic
      ? headerLabels.branch_2
      : headerLabels.branch,
    headerLabels.manager,
  ];
  switch (section) {
    case ic:
      return [
        ...commonHeaderLabels,
        headerLabels.ic_start,
        headerLabels.phone_number,
        headerLabels.seniority_at_davidson,
        headerLabels.salary,
        headerLabels.last_fee,
        headerLabels.nat,
        headerLabels.job,
        headerLabels.email,
      ];
    case fdm:
      return [
        ...commonHeaderLabels,
        headerLabels.end_of_mission,
        headerLabels.phone_number,
        headerLabels.seniority_at_davidson,
        headerLabels.salary,
        headerLabels.AT_fee,
        headerLabels.nat,
        headerLabels.job,
        headerLabels.email,
      ];
    case ed:
      return [
        ...commonHeaderLabels,
        headerLabels.start_date,
        headerLabels.phone_number,
        headerLabels.mobility,
        headerLabels.salary,
        headerLabels.nat,
        headerLabels.job,
        headerLabels.email,
      ];
    case ko:
      return [...commonHeaderLabels, headerLabels.leaving_date_expected];
    case cl:
      return [
        ...commonHeaderLabels,
        headerLabels.long_leave_start_date,
        headerLabels.provisional_return_date,
      ];
    case ok:
      return [
        ...commonHeaderLabels,
        headerLabels.start_up_at,
        headerLabels.date_of_start_up,
        headerLabels.mission_name,
        headerLabels.client,
        headerLabels.end_date,
      ];
    default:
      break;
  }
};

export const seniorityFormatter = ({ seniorityValue, lang }) => {
  return `${seniorityValue === 0 ? "< 1" : seniorityValue} ${
    lang === "fr" ? "an" : "year"
  }${seniorityValue > 1 ? "s" : ""}`;
};

export const consultantNameFormatter = ({
  firstName,
  lastName,
  dybId,
  workerType,
  isAllWorkerTypeRequest,
}) => {
  const fullName = `${firstName} ${lastName}${
    isAllWorkerTypeRequest && workerType === "subcontractor" ? " (STR)" : ""
  }`;
  return dybId ? (
    <a
      href={buildDybProfileUrl(dybId)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {fullName}
    </a>
  ) : (
    fullName
  );
};

export const currencyHandler = (value) => (value === "-" ? "€" : value);

export const conditionHelper = ({ conditionParam, conditionReturn }) =>
  conditionParam && conditionReturn;

export const dateFormatter = ({ lang, date }) =>
  date ? getFormattedDate(date, lang) : "-";

export const requestValueFormatter = (array) => array?.map((e) => e.value);

export const formattedData = ({
  data,
  lang,
  mutualDashboardICData,
  mutualDashboardFDMData,
  mutualDashboardEDData,
  mutualDashboardOKData,
  mutualDashboardCLData,
  mutualDashboardExitsData,
  userScope,
  companiesExitsCompaniesBy,
  namesByFilters,
}) => {
  const dataTypeCheck = {
    icData: data === mutualDashboardICData.data,
    fdmData: data === mutualDashboardFDMData.data,
    edData: data === mutualDashboardEDData.data,
    exitsData: data === mutualDashboardExitsData.data,
    okData: data === mutualDashboardOKData.data,
    clData: data === mutualDashboardCLData.data,
  };

  const { icData, fdmData, edData, exitsData, okData, clData } = dataTypeCheck;

  return (
    data &&
    data.length > 0 &&
    data.map((elm) => ({
      card:
        getEntityIsAllowed(elm.worker_id, namesByFilters) === false ? (
          <span>{elm.worker_id}</span>
        ) : (
          <a
            href={`/${userScope}/${elm.worker_type || "consultant"}/${
              elm.worker_slug
            }`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {elm.worker_id}
          </a>
        ),
      consultant: consultantNameFormatter({
        firstName: elm.worker_first_name,
        lastName: elm.worker_last_name,
        dybId: elm.worker_dyb_showcase_id,
        workerType: elm.worker_type,
        isAllWorkerTypeRequest: companiesExitsCompaniesBy === "all",
      }),
      branch: elm.worker_branch_name || elm.mission_branch_name,
      manager: okData
        ? elm.worker_manager_name
        : elm.mission_manager_name || elm.worker_manager_name,
      ...conditionHelper({
        conditionParam: icData,
        conditionReturn: {
          ic_start: dateFormatter({ lang, date: elm.worker_ic_start_date }),
        },
      }),
      ...conditionHelper({
        conditionParam: fdmData,
        conditionReturn: {
          end_of_mission: dateFormatter({
            lang,
            date: elm.mission_end_date,
          }),
        },
      }),
      ...conditionHelper({
        conditionParam: edData,
        conditionReturn: {
          start_date: dateFormatter({
            lang,
            date: elm.worker_contract_start_date,
          }),
        },
      }),
      ...conditionHelper({
        conditionParam: icData || fdmData || edData,
        conditionReturn: { phone_number: elm.worker_phone_number },
      }),
      ...conditionHelper({
        conditionParam: edData,
        conditionReturn: {
          mobility: elm?.worker_mobility?.join(", ") || "-",
        },
      }),
      ...conditionHelper({
        conditionParam: icData || fdmData,
        conditionReturn: {
          seniority_at_davidson: seniorityFormatter({
            seniorityValue: elm.worker_seniority,
            lang,
          }),
        },
      }),
      ...conditionHelper({
        conditionParam: icData || fdmData || edData,
        conditionReturn: {
          salary:
            elm.worker_salary + currencyHandler(elm.worker_salary_currency),
        },
      }),
      ...conditionHelper({
        conditionParam: icData,
        conditionReturn: {
          last_fee: `${
            elm.worker_last_tjm
              ? elm.worker_last_tjm +
                currencyHandler(elm.worker_last_tjm_currency)
              : "-"
          }`,
        },
      }),
      ...conditionHelper({
        conditionParam: fdmData,
        conditionReturn: {
          at_fee: elm.mission_billed_rate + elm.mission_billed_rate_currency,
        },
      }),
      ...conditionHelper({
        conditionParam: icData || fdmData || edData,
        conditionReturn: {
          nat: elm.worker_nationality || elm.worker_nat,
          job:
            elm.worker_domain_name ||
            elm.mission_domain ||
            elm.mission_domain_name,
          email: (
            <a
              href={`mailto:${elm.worker_email}`}
              style={{ textDecoration: "none" }}
            >
              @
            </a>
          ),
        },
      }),
      ...conditionHelper({
        conditionParam: okData,
        conditionReturn: {
          start_up_at: elm.mission_manager_name,
          date_of_start_up: dateFormatter({
            lang,
            date: elm.mission_start_date,
          }),
          mission_name: elm.mission_name,
          client: elm.mission_client_name,
          end_date: dateFormatter({ lang, date: elm.mission_end_date }),
        },
      }),
      ...conditionHelper({
        conditionParam: clData,
        conditionReturn: {
          long_leave_start_date: dateFormatter({
            lang,
            date: elm.longleave_start_date,
          }),
          provisional_return_date: dateFormatter({
            lang,
            date: elm.longleave_end_date,
          }),
        },
      }),
      ...conditionHelper({
        conditionParam: exitsData,
        conditionReturn: {
          leaving_date_expected: dateFormatter({
            lang,
            date: elm.worker_contract_end_date,
          }),
        },
      }),
    }))
  );
};

const renderInputDate = ({ infos, lang, setValue, date }) => {
  return (
    <InputDate
      id={`${infos.sectionName}`}
      label={infos.inputDateLabel}
      name={`${infos.inputName}`}
      value={date}
      onChange={(e) => setValue(`${infos.inputName}`, e.target.value)}
      lang={lang}
      min={moment(new Date()).format("MM/DD/YYYY")}
    />
  );
};

export const sharedDashboardInputs = ({
  infos,
  renderInputBranch,
  setValue,
  SHARED_DASHBOARD_FORM_LABELS,
  branchList,
  watch,
  control,
  register,
  renderInputManager,
  getOptions,
  personsByProfileList,
  section,
  jobList,
  jobCategoryList,
  getCategoriesJobIds,
  renderInputJob,
  renderInputJobCategoryTag,
  lang,
  errors,
  VAR_TYPES,
  EXITS_COMPANIES_BY,
  getNamesByFilters,
  namesByFilters,
}) => {
  const { sectionName, filters } = infos || {};
  const {
    subsidiary,
    manager,
    start_at,
    job,
    jobCategory,
    mobility,
    exits_companies_by,
    date,
  } = filters || {};

  // The picked categories narrow the job filter, as on every other screen.
  const jobOptions = getJobsInCategories(
    jobList,
    getCategoriesJobIds((jobCategory || []).map((elm) => elm?.value))
  );

  // Drop the jobs the categories no longer offer, removing the last category
  // included (redmine 12939). Done in the gesture that changes the categories,
  // not in an effect reacting to it: SharedDashboard restores the form from the
  // stored filters, and React runs a child's effects BEFORE its parent's — so a
  // prune done in an effect was overwritten by the restore that followed.
  const setJobCategories = (_name, categories) => {
    setValue(`${sectionName}_domain_categories`, categories);
    const selectedJobs = job || [];
    if (!selectedJobs.length) return;
    const categoryIds = (categories || [])
      .map((elm) => elm?.value)
      .filter(Boolean);
    const kept = categoryIds.length
      ? getJobsInCategories(selectedJobs, getCategoriesJobIds(categoryIds))
      : [];
    if (kept.length !== selectedJobs.length) {
      setValue(`${sectionName}_domains`, kept);
    }
  };

  return (
    <>
      <div className="cell-padding subsidiary-input">
        {renderInputBranch({
          filter: `${sectionName}_subsidiaries`,
          selectedOptions: subsidiary?.filter((e) => e?.value),
          setValue,
          label: SHARED_DASHBOARD_FORM_LABELS.subsidiary,
          branches: branchList,
          watch,
          limitChoice: 99,
          control,
          register,
          required: false,
        })}
      </div>
      <div className="cell-padding manager-input">
        {renderInputManager({
          control,
          getOptions,
          label: SHARED_DASHBOARD_FORM_LABELS.manager_name,
          personsByProfileList,
          register,
          limitChoice: 99,
          filter: `${sectionName}_managers`,
          selectedOptions: manager,
          setValue,
          required: false,
          getNamesByFilters,
          namesByFilters,
        })}
      </div>
      {section === ok && (
        <div className="cell-padding manager-input">
          {renderInputManager({
            control,
            getOptions,
            personsByProfileList,
            register,
            limitChoice: 99,
            filter: "ok_start_at",
            selectedOptions: start_at,
            label: SHARED_DASHBOARD_FORM_LABELS.start_at,
            setValue,
            required: false,
            getNamesByFilters: getNamesByFilters,
            namesByFilters: namesByFilters,
          })}
        </div>
      )}
      <div className="cell-padding date-input">
        {renderInputDate({ infos, lang, setValue, date })}
      </div>
      {(section === ic || section === fdm || section === ed) && (
        <>
          <div className="cell-padding function-input">
            {renderInputJobCategoryTag({
              filter: `${sectionName}_domain_categories`,
              selectedOptions: jobCategory,
              control,
              jobCategories: jobCategoryList,
              label: JOB_CATEGORY_LABEL,
              register,
              setValue: setJobCategories,
              limitChoice: 99,
              errors,
              lang,
            })}
          </div>
          <div className="cell-padding function-input">
            {renderInputJob({
              filter: `${sectionName}_domains`,
              selectedOptions: job,
              control,
              davFunctions: jobOptions,
              label: SHARED_DASHBOARD_FORM_LABELS.dav_function,
              register,
              setValue,
              limitChoice: 99,
              required: false,
              lang,
              // The category filter comes first: it is what narrows this list
              // (redmine 12939). Applies to the three sections that carry both
              // filters — intercontract, mission exits and direct hires.
              lockedUntilCategory: (jobCategory || []).length === 0,
            })}
          </div>
        </>
      )}
      {section === ed && (
        <div className="cell-padding mobility-input">
          <Controller
            {...{
              control,
              register,
              name: "mobility",
              render: (props) => (
                <InputText
                  id="mobility"
                  errors={errors["mobility"]}
                  label={SHARED_DASHBOARD_FORM_LABELS.mobility}
                  type={VAR_TYPES.TEXT}
                  value={mobility}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  {...props}
                />
              ),
            }}
          />
        </div>
      )}
      {section === ko && (
        <div className="cell-padding companies-exits-input">
          <Controller
            {...{
              control,
              name: "exits_companies_by",
              register,
              render: (props) => (
                <InputSelect
                  errors={{}}
                  label={SHARED_DASHBOARD_FORM_LABELS.exits_companies_by}
                  id="exits_companies_by"
                  options={EXITS_COMPANIES_BY}
                  value={exits_companies_by}
                  setValue={setValue}
                  {...props}
                />
              ),
            }}
          />
        </div>
      )}
    </>
  );
};

export const formattedParams = ({ filters, section }) => {
  const {
    date,
    job,
    jobCategory,
    manager,
    subsidiary,
    exits_companies_by,
    start_at,
  } = filters || {};

  const mapHelper = (array) => array?.map((e) => e?.value);

  const paramsSettings = [
    { param: { date }, sections: [ic, fdm, ed, ok, cl, ko] },
    {
      param: { mission_signed_by_ids: mapHelper(subsidiary) },
      sections: [fdm],
    },
    {
      param: { managers: mapHelper(manager) },
      sections: [ic, fdm, ed, ok, cl, ko],
    },
    {
      param: { domain_categories: mapHelper(jobCategory) },
      sections: [ic, fdm, ed],
    },
    {
      param: { domains: mapHelper(job) },
      sections: [ic, fdm, ed],
    },
    {
      param: { assigned_branch_ids: mapHelper(subsidiary) },
      sections: [ic, ed, ok, cl, ko],
    },
    {
      param: { first_managers: mapHelper(start_at) },
      sections: [ok],
    },
    {
      param: {
        worker_type: exits_companies_by === "all" ? "" : exits_companies_by,
      },
      sections: [ko],
    },
  ];

  const params = paramsSettings
    ?.filter((e) => e.sections?.includes(section))
    ?.map((e) => e?.param)
    .reduce((acc, obj) => ({ ...acc, ...obj }), {});

  return params;
};

export const currentDate = moment();
export const currentDateFormatted = currentDate.format("YYYY-MM-DD");
export const lastDayOfTheMonthFormatted = currentDate
  .endOf("month")
  .format("YYYY-MM-DD");
export const currentDatePlusOneMonth = currentDate.add(1, "months");
export const currentDatePlusOneMonthFormatted =
  currentDatePlusOneMonth.format("YYYY-MM-DD");
export const firstMondayOfNextMonthFormatted = () => {
  const now = moment();
  const firstDayOfNextMonth = moment(now).add(1, "months").startOf("month");
  const dayOfWeek = firstDayOfNextMonth.day();
  const offset = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  const firstMondayOfNextMonth = firstDayOfNextMonth.add(offset, "days");
  return firstMondayOfNextMonth.format("YYYY/MM/DD");
};
